import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'

import type { ExceptionsFilter } from '../hooks/useApi'
import type { ReasonCode, Severity } from '../api/types'
import { useReasonCodes } from '../hooks/useApi'
import { tokens } from '../theme'

const severities: Severity[] = ['critical', 'high', 'warn', 'info']
const subjectTypes: Array<{ value: '' | 'job' | 'engineer'; label: string }> = [
  { value: '', label: 'All' },
  { value: 'job', label: 'Jobs' },
  { value: 'engineer', label: 'Engineers' },
]

const sevColour: Record<Severity, string> = {
  critical: tokens.alert,
  high: '#E09935',
  warn: tokens.gold,
  info: tokens.textSec,
}

/**
 * Exception-list filter toolbar. One horizontal row on desktop, stacks
 * cleanly on mobile. Three filter kinds, each with its own field label
 * above a control:
 *   - Severity: multi-select segmented buttons (colored when active)
 *   - Subject:  single-select segmented buttons
 *   - Code:     multi-select autocomplete (searchable — hides the 12+
 *               reason-code chip wrap that was cluttering the page)
 * A "Clear" button appears when any filter is active.
 */
export function Filters({
  filter,
  onChange,
}: {
  filter: ExceptionsFilter
  onChange: (next: ExceptionsFilter) => void
}) {
  const { data: reasonCodes } = useReasonCodes()
  const codes = reasonCodes ?? []
  const activeCodes = filter.code ?? []
  const activeSeverities = (filter.severity ?? []) as Severity[]

  const hasFilters =
    activeSeverities.length > 0 ||
    activeCodes.length > 0 ||
    (filter.subject_type ?? '') !== ''

  const handleSeverity = (_: unknown, next: Severity[]) => {
    onChange({ ...filter, severity: next.length ? next : undefined })
  }
  const handleSubject = (_: unknown, next: string | null) => {
    if (next === null) return // exclusive group ignores clicks that would deselect
    onChange({ ...filter, subject_type: next === '' ? undefined : (next as 'job' | 'engineer') })
  }
  const handleCodes = (_: unknown, next: ReasonCode[]) => {
    onChange({ ...filter, code: next.length ? next.map((c) => c.code) : undefined })
  }
  const clear = () => onChange({})

  const selectedCodeOptions = codes.filter((c) => activeCodes.includes(c.code))

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={{ xs: 2, md: 2 }}
      sx={{ alignItems: { md: 'flex-end' }, flexWrap: 'wrap' }}
    >
      <FilterField label="Severity">
        <ToggleButtonGroup
          size="small"
          value={activeSeverities}
          onChange={handleSeverity}
          aria-label="Filter by severity"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 1.5,
              py: 0.4,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.4,
              borderColor: 'divider',
            },
            ...severities.reduce((acc, s) => {
              acc[`& .MuiToggleButton-root[value="${s}"].Mui-selected`] = {
                bgcolor: alpha(sevColour[s], 0.14),
                color: sevColour[s],
                borderColor: alpha(sevColour[s], 0.4),
                '&:hover': { bgcolor: alpha(sevColour[s], 0.22) },
              }
              return acc
            }, {} as Record<string, object>),
          }}
        >
          {severities.map((s) => (
            <ToggleButton key={s} value={s} aria-label={s}>
              {capitalize(s)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </FilterField>

      <FilterField label="Subject">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter.subject_type ?? ''}
          onChange={handleSubject}
          aria-label="Filter by subject"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 1.75,
              py: 0.4,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.4,
              borderColor: 'divider',
            },
            '& .MuiToggleButton-root.Mui-selected': {
              bgcolor: alpha(tokens.indigo, 0.12),
              color: tokens.indigo,
              borderColor: alpha(tokens.indigo, 0.4),
              '&:hover': { bgcolor: alpha(tokens.indigo, 0.18) },
            },
          }}
        >
          {subjectTypes.map((s) => (
            <ToggleButton key={s.value || 'all'} value={s.value}>
              {s.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </FilterField>

      <FilterField label={`Code${activeCodes.length ? ` · ${activeCodes.length}` : ''}`} sx={{ flex: 1, minWidth: 240 }}>
        <Autocomplete
          multiple
          size="small"
          options={codes}
          value={selectedCodeOptions}
          onChange={handleCodes}
          getOptionLabel={(o) => o.code}
          isOptionEqualToValue={(o, v) => o.code === v.code}
          disableCloseOnSelect
          renderOption={(props, option) => {
            const { key, ...rest } = props as { key?: React.Key } & React.HTMLAttributes<HTMLLIElement>
            return (
              <Box component="li" key={key} {...rest}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.indigo }}>
                    {option.code}
                  </Typography>
                  <Typography component="span" sx={{ fontSize: 11.5, color: 'text.secondary', ml: 'auto' }}>
                    {option.category}
                  </Typography>
                </Box>
              </Box>
            )
          }}
          renderValue={(value: ReasonCode[], getItemProps) =>
            value.map((option, index) => {
              const { key, ...itemProps } = getItemProps({ index })
              return (
                <Chip
                  key={key}
                  label={option.code}
                  size="small"
                  variant="outlined"
                  {...itemProps}
                  sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 600 }}
                />
              )
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={activeCodes.length ? '' : 'Search reason codes…'}
              sx={{ '& .MuiInputBase-root': { minHeight: 36 } }}
            />
          )}
        />
      </FilterField>

      {hasFilters && (
        <Button
          size="small"
          variant="text"
          onClick={clear}
          sx={{ textTransform: 'none', color: 'text.secondary', alignSelf: { xs: 'flex-start', md: 'flex-end' }, mb: { md: 0.25 } }}
        >
          Clear filters
        </Button>
      )}
    </Stack>
  )
}

function FilterField({
  label,
  children,
  sx,
}: {
  label: string
  children: React.ReactNode
  sx?: object
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ...sx }}>
      <Typography
        component="label"
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.09em',
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  )
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
